import React from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Calendar, Clock, Bot, Video, User } from "lucide-react";
import { FaPlus } from "react-icons/fa";
import { AI_LAB_COURSES, TIME_SLOTS } from "../../constants/ai-lab-constants";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../config/firebaseConfig";

const EnrolledChildrenSection = ({ enrollments }) => {
  const navigate = useNavigate();

  const [batches, setBatches] = React.useState([]);

  React.useEffect(() => {
    const fetchBatches = async () => {
      try {
        const snap = await getDocs(collection(db, "ai-lab-batches"));
        setBatches(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Failed to fetch batches:", err);
      }
    };
    fetchBatches();
  }, []);

  // Map course ID to readable labels
  const getCourseLabel = (id) => {
    const course = AI_LAB_COURSES.find(c => c.id === id);
    return course ? course.label : id;
  };

  // Group enrollments by child (name + grade key)
  const childrenMap = {};
  enrollments.forEach(enrollment => {
    (enrollment.children || []).forEach(child => {
      const key = `${child.name.trim().toLowerCase()}_${child.grade}`;
      if (!childrenMap[key]) {
        childrenMap[key] = {
          name: child.name,
          grade: child.grade,
          school: child.school,
          courses: []
        };
      }
      (child.interests || []).forEach(courseId => {
        const sched = child.schedule?.[courseId];
        childrenMap[key].courses.push({
          courseId,
          day: sched?.day || "To be confirmed",
          timeSlot: sched?.timeSlot || "",
          paymentId: enrollment.paymentId || "N/A",
          enrolmentId: enrollment.id
        });
      });
    });
  });

  const enrolledChildren = Object.values(childrenMap);

  // Helper function to check if the current time is within 15 minutes of class start or during the class
  const isClassActive = (day, timeSlot) => {
    try {
      if (!day || !timeSlot || day === "To be confirmed") return false;

      const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      const now = new Date();
      const currentDayName = daysOfWeek[now.getDay()];

      // Check if it is the correct day
      if (currentDayName.toLowerCase() !== day.trim().toLowerCase()) {
        return false;
      }

      // Split by any dash character: hyphen (-), en-dash (–), or em-dash (—)
      const parts = timeSlot.split(/\s*[-–—]\s*/);
      const timePart = parts[0]?.trim();
      const match = timePart.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
      if (!match) return false;

      let hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const ampm = match[3].toUpperCase();
      if (ampm === "PM" && hours < 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;

      const classStart = new Date();
      classStart.setHours(hours, minutes, 0, 0);

      // Default duration is 1 hour
      let classEnd = new Date(classStart.getTime() + 60 * 60 * 1000);
      const endPart = parts[1]?.trim();
      const endMatch = endPart?.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
      if (endMatch) {
        let endHours = parseInt(endMatch[1], 10);
        const endMinutes = parseInt(endMatch[2], 10);
        const endAmpm = endMatch[3].toUpperCase();
        if (endAmpm === "PM" && endHours < 12) endHours += 12;
        if (endAmpm === "AM" && endHours === 12) endHours = 0;
        classEnd = new Date();
        classEnd.setHours(endHours, endMinutes, 0, 0);
      }

      const fifteenMinsMs = 15 * 60 * 1000;
      const nowTime = now.getTime();

      // Active if it is 15 minutes before the start time until the class ends
      return nowTime >= (classStart.getTime() - fifteenMinsMs) && nowTime <= classEnd.getTime();
    } catch (e) {
      console.error("Error parsing class active state:", e);
      return false;
    }
  };

  const getJoinLink = (childName, courseInfo, course) => {
    // 1. Find a matching batch from Firestore
    const matchingBatch = batches.find(b => {
      const batchTimeLabel = TIME_SLOTS.find(t => t.id === b.timeSlot)?.label || b.timeSlot;
      return b.courseId === course.courseId && 
             b.day === course.day && 
             batchTimeLabel === course.timeSlot;
    });

    if (matchingBatch && matchingBatch.meetLink) {
      return matchingBatch.meetLink;
    }

    // 2. Fallback to WhatsApp if no batch is found
    const scheduleStr = `${course.day} at ${course.timeSlot}`;
    const message = `Hello, I'd like to join the live class for ${childName} enroled in ${courseInfo?.label || getCourseLabel(course.courseId)} (${scheduleStr}).`;
    return `https://wa.me/919560734660?text=${encodeURIComponent(message)}`;
  };

  const avatarStyles = [
    { background: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)", color: "#ffffff" }, // Purple
    { background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", color: "#ffffff" }, // Amber
    { background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#ffffff" }, // Emerald
    { background: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)", color: "#ffffff" }, // Pink
    { background: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)", color: "#ffffff" }, // Cyan
  ];

  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 shadow-sm space-y-4 text-left relative overflow-hidden">
      {/* Visual Accent Top Bar */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-600" />

      {/* Card Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold border border-blue-100">
            <Sparkles className="w-3 h-3 text-amber-500 animate-pulse" />
            <span>AI Lab Active Program</span>
          </div>
          <h3 className="text-lg md:text-xl font-black text-slate-800 tracking-tight">
            My Children's AI Lab Courses
          </h3>
        </div>
        <div>
          <button
            onClick={() => navigate("/parentDashboard/ai-lab/enrol")}
            className="px-4 py-2 text-white rounded-xl text-xs sm:text-sm font-extrabold shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 flex items-center gap-2"
            style={{ background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)" }}
          >
            <FaPlus className="w-3 h-3 text-white" /> Enrol More Children
          </button>
        </div>
      </div>

      {/* Children Cards Grid inside the Main Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
        {enrolledChildren.map((child, idx) => (
          <div key={idx} className="bg-gradient-to-b from-white to-slate-50/40 border border-slate-200 rounded-2xl flex flex-col hover:border-blue-200 hover:shadow-sm transition-all duration-300">
            {/* Child Header */}
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/35 rounded-t-2xl">
              <div className="flex items-center gap-2.5">
                <div 
                  className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm shadow-sm"
                  style={avatarStyles[idx % avatarStyles.length]}
                >
                  {child.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4 className="font-extrabold text-slate-800 text-sm sm:text-base leading-tight">
                    {child.name}
                  </h4>
                  <span className="inline-block bg-slate-100 text-slate-600 font-bold px-2 py-0.2 rounded text-[9px] uppercase tracking-wider mt-0.5">
                    {child.grade}
                  </span>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
                ACTIVE
              </span>
            </div>

            {/* Child Body */}
            <div className="p-4 flex-1 space-y-3">
              {child.courses.map((course, cIdx) => {
                const active = isClassActive(course.day, course.timeSlot);
                const scheduleStr = `${course.day} at ${course.timeSlot}`;
                const courseInfo = AI_LAB_COURSES.find(c => c.id === course.courseId);
                const matchingBatch = batches.find(b => {
                  const batchTimeLabel = TIME_SLOTS.find(t => t.id === b.timeSlot)?.label || b.timeSlot;
                  return b.courseId === course.courseId && 
                         b.day === course.day && 
                         batchTimeLabel === course.timeSlot;
                });
                
                
                return (
                  <div 
                    key={cIdx} 
                    className="bg-white border rounded-2xl p-4 space-y-3 shadow-sm hover:shadow-md transition-shadow duration-200"
                    style={{ borderColor: courseInfo?.border || '#e2e8f0' }}
                  >
                    <div className="flex items-center gap-2.5">
                      <div 
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-base shadow-sm"
                        style={{ backgroundColor: courseInfo?.bg || '#f1f5f9' }}
                      >
                        {courseInfo?.icon || "📘"}
                      </div>
                      <span className="font-extrabold text-slate-800 text-sm sm:text-base">
                        {courseInfo?.label || getCourseLabel(course.courseId)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 pl-9.5 text-xs text-slate-600 font-semibold">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      <span>
                        {course.day}{course.timeSlot ? `, ${course.timeSlot}` : ""}
                      </span>
                    </div>

                    {matchingBatch && matchingBatch.teacherName && (
                      <div className="flex items-center gap-2 pl-[38px] text-xs text-slate-600 font-semibold pt-1">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        <span>Teacher: {matchingBatch.teacherName}</span>
                      </div>
                    )}

                    <div className="pt-1">
                      {active ? (
                        <a
                          href={getJoinLink(child.name, courseInfo, course)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full py-2 text-white rounded-xl text-xs font-bold transition-all duration-300 flex items-center justify-center gap-2 shadow-sm transform hover:-translate-y-0.5 active:translate-y-0"
                          style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
                        >
                          <Video className="w-3.5 h-3.5 animate-bounce" /> Join Live Class
                        </a>
                      ) : (
                        <button
                          disabled
                          className="w-full py-2 bg-slate-50 text-slate-400 border border-slate-200 rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-not-allowed"
                        >
                          <Clock className="w-3.5 h-3.5 text-slate-300" /> Join link active 15m before class
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EnrolledChildrenSection;
